import { Col, Flex, Form, Modal, Row, Tag, Typography } from "antd";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { setTitle } from "../../../../redux/slices/configuracoes/configuracoesSlice";
import { atualizarPessoaAtivo, buscarPessoa, deletarPessoa, listarCategoriaPessoa } from "../../../../services/pessoaService";
import { setBuscaPessoa, setEntityPessoa, setModalDeletarPessoa, setPagePessoa } from "../../../../redux/slices/pessoa/pessoaSlice";
import { PessoaModel } from "../../../../models/pessoa/pessoaModel";
import { AtivoModel } from "../../../../models/ativoModel";
import { BuscarPessoaModel } from "../../../../models/pessoa/chamadas/buscarPessoaModel";
import { ItemMenuAcoes } from "../../../../models/sistema/itemMenuAcoes";
import * as Icon from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { formatCPFCNPJ } from "../../../../util/formats";
import AcoesComponent from "../../../../components/table/acoesComponent";
import Search from "antd/es/input/Search";
import { ButtonComponent } from "../../../../components/button/cadastrar";
import TableComponent from "../../../../components/table/tableComponent";
import MensagemModal from "../../../../components/modal/mensagem";
import { SwitchComponent } from "../../../../components/button/switch";

const GeralPage = () => {
  //estados globais
  const pessoaState = useAppSelector((state) => state.pessoa);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(setTitle("PESSOAS"));
    dispatch(buscarPessoa(pessoaState.busca));
    dispatch(listarCategoriaPessoa());
    form.setFieldsValue({ pesquisar: pessoaState.busca.pesquisar });
  }, []);

  const goCadastro = () => {
    dispatch(setEntityPessoa({}));
    dispatch(setPagePessoa("cadastrar"));
  };

  const goEditar = (value: PessoaModel) => {
    dispatch(setEntityPessoa(value));
    dispatch(setPagePessoa("cadastrar"));
  };

  const goDeletar = (value: PessoaModel) => {
    dispatch(setEntityPessoa(value));
    dispatch(setModalDeletarPessoa(true));
  };

  const closeModalDeletar = () => {
    dispatch(setModalDeletarPessoa(false));
  };

  const changeAtivo = async (pessoa: PessoaModel) => {
    const args: AtivoModel = {
      id: pessoa.id,
      ativo: !pessoa.ativo,
    };
    dispatch(atualizarPessoaAtivo({ value: args, busca: pessoaState.busca }));
  };

  const onSearch = (value: string) => {
    let args: BuscarPessoaModel = { ...pessoaState.busca, pesquisar: value.replace(/\-/g, "") };

    dispatch(setBuscaPessoa(args));
    dispatch(buscarPessoa(args));
  };

  const categoriaFiltrada = pessoaState.listCategoria.filter((a: any) => !a.denominacao.includes("Consignatário"));

  const filtroCategoria = () =>
    Array.from(
      new Set<any>(
        categoriaFiltrada.map((a: any) => ({
          text: a.denominacao,
          value: a.denominacao,
        }))
      )
    );

  const items: ItemMenuAcoes[] = [
    {
      label: "Editar",
      key: "1",
      icon: <Icon.EditOutlined />,
      function: (item: any) => goEditar(item),
    },
    {
      label: "Remover",
      key: "2",
      icon: <Icon.DeleteOutlined />,
      function: (item: any) => goDeletar(item),
    },
  ];

  const columns: ColumnsType<PessoaModel> = [
    {
      title: "NOME FANTASIA",
      dataIndex: "apelido",
      sorter: (a: any, b: any) => a.apelido.localeCompare(b.apelido),
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => (
        <Typography.Text style={{ width: 200 }} ellipsis={{ tooltip: record.apelido }}>
          {record.apelido}
        </Typography.Text>
      ),
    },
    {
      title: "RAZÃO SOCIAL",
      dataIndex: "razaoSocial",
      sorter: (a: any, b: any) => a.razaoSocial.localeCompare(b.razaoSocial),
      sortDirections: ["ascend", "descend", "ascend"],
      render: (_, record) => (
        <Typography.Text style={{ width: 200 }} ellipsis={{ tooltip: record.razaoSocial }}>
          {record.razaoSocial}
        </Typography.Text>
      ),
    },
    {
      title: "CNPJ / CPF",
      dataIndex: "cpfcnpj",
      align: "right",
      render: (_, record) => record.cpfcnpj && formatCPFCNPJ(record.cpfcnpj),
    },
    {
      title: "CIDADE",
      dataIndex: "cidade",
      sorter: (a: any, b: any) => a.cidade?.localeCompare(b.cidade),
      sortDirections: ["ascend", "descend", "ascend"],
    },
    {
      title: "CATEGORIA",
      dataIndex: "denominacaoCategoria",
      align: "center",
      filters: filtroCategoria(),
      onFilter: (value: any, record: any) => record.denominacaoCategoria.includes(value),
      render: (_, record) => record.denominacaoCategoria.map((a: string) => <Tag key={a}>{a}</Tag>),
    },
    {
      title: "TIPO",
      dataIndex: "tipoPessoa",
      align: "center",
      filters: [
        {
          text: "PF",
          value: "PF" as any,
        },
        {
          text: "PJ",
          value: "PJ" as any,
        },
      ],
      onFilter: (value: any, record: any) => record.tipoPessoa.toString().includes(value),
      render: (_, record) => {
        return <>{record.tipoPessoa === "PF" ? <div className="circlePV">PF</div> : <div className="circlePR">PJ</div>}</>;
      },
    },
    {
      title: "ATIVO",
      dataIndex: "ativo",
      align: "center",
      width: 100,
      filters: [
        {
          text: "Ativo",
          value: "true" as any,
        },
        {
          text: "Inativo",
          value: "false" as any,
        },
      ],
      onFilter: (value: any, record: any) => record.ativo.toString().includes(value),
      defaultFilteredValue: ["true" as any],
      render: (_, record) => <SwitchComponent checked={record.ativo} onChange={() => changeAtivo(record)} />,
    },
    {
      title: "AÇÕES",
      dataIndex: "acoes",
      align: "center",
      width: 75,
      render: (_, record) => <AcoesComponent record={record} items={items} />,
    },
  ];

  return (
    <Flex vertical>
      <Form layout="vertical" form={form}>
        <Row gutter={12} align="bottom">
          <Col span={20}>
            <Flex gap={12}>
              <Form.Item label="Pesquisar" name="pesquisar">
                <Search allowClear placeholder="Nome Fantasia, Razão Social ou CPF/CNPJ" onSearch={onSearch} className="searchInput" enterButton style={{ width: 400 }} />
              </Form.Item>
            </Flex>
          </Col>
          <Col span={4}>
            <Flex justify="end">
              <ButtonComponent onClick={goCadastro} />
            </Flex>
          </Col>
        </Row>
      </Form>
      <TableComponent data={pessoaState.list} columns={columns} loading={pessoaState.loading} />
      <Modal title={"REMOVER PESSOA"} centered width={350} open={pessoaState.modalDeletar} onCancel={closeModalDeletar} footer={null} destroyOnHidden>
        <MensagemModal text="Deseja remover o cliente/fornecedor?" action={deletarPessoa({ value: pessoaState.entity.id, busca: pessoaState.busca })} close={setModalDeletarPessoa(false)} />
      </Modal>
    </Flex>
  );
};

export default GeralPage;
